import { useMemo, useRef } from "react"
import type { BinaryOperator, Filter, Query } from "@cubejs-client/core"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"

// Variant-grain feed cube: one row per Shopify variant (color × size). The
// product drawer lists a product's variants from it (MD-5394) so a feed
// editor can pick media per variant; the Mega Feed table itself stays at
// product grain.
export const VARIANTS_CUBE = "company_product_feed_shopify_by_product_variant"

const DIMENSIONS = [
  "id",
  "item_group_id",
  "item_group_id_color",
  "title",
  "color",
  "size",
  "image_link",
  "availability",
  "quantity",
  "price",
  "sale_price",
  "price_currency",
  "id_sku",
  "link",
]

// Enough for any real product — Shopify caps a product at 100 variants
// (2,048 on the new limits, which no feed editor will page through).
const MAX_VARIANTS = 500

export interface TProductVariant {
  id: string
  itemGroupId: string
  /** `<productId>_<color>` — the feed ref a colorway's media selection is
   *  keyed by; empty when the product has no color option. */
  colorRef: string
  title: string
  /** Empty when the product has no color option. */
  color: string
  /** Empty when the product has no size option. */
  size: string
  imageUrl: string
  available: boolean
  quantity: number
  price: number | null
  salePrice: number | null
  currency: string
  sku: string
  link: string
}

/** Variants sharing one color — the unit a catalog card shows. */
export interface TVariantGroup {
  /** The color, or "" for a product without a color option. */
  color: string
  /** The group's feed ref (`<productId>_<color>`); "" without a color option. */
  colorRef: string
  /** The group's image: the first variant's own image. */
  imageUrl: string
  variants: TProductVariant[]
  available: boolean
  quantity: number
}

const asText = (value: unknown) => String(value ?? "").trim()
const asNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}
const asBool = (value: unknown) =>
  value === true || value === "true" || value === 1

export function toVariant(
  row: Record<string, unknown>,
  prefix = VARIANTS_CUBE,
): TProductVariant | null {
  const get = (key: string) => row[`${prefix}.${key}`]
  const id = asText(get("id"))
  if (!id) return null
  return {
    id,
    itemGroupId: asText(get("item_group_id")),
    colorRef: asText(get("item_group_id_color")),
    title: asText(get("title")),
    color: asText(get("color")),
    size: asText(get("size")),
    imageUrl: asText(get("image_link")),
    available: asBool(get("availability")),
    quantity: asNumber(get("quantity")) ?? 0,
    price: asNumber(get("price")),
    salePrice: asNumber(get("sale_price")),
    currency: asText(get("price_currency")),
    sku: asText(get("id_sku")),
    link: asText(get("link")),
  }
}

/**
 * Group variants by color, keeping the feed's order (Shopify variant ids
 * ascend in the merchant's listed order, so sizes come out as the merchant
 * listed them). A product without a color option is one group with color "".
 */
export function groupVariantsByColor(
  variants: TProductVariant[],
): TVariantGroup[] {
  const groups = new Map<string, TVariantGroup>()
  for (const variant of variants) {
    const key = variant.colorRef || variant.color
    const group = groups.get(key) ?? {
      color: variant.color,
      colorRef: variant.colorRef,
      imageUrl: variant.imageUrl,
      variants: [],
      available: false,
      quantity: 0,
    }
    group.variants.push(variant)
    group.available = group.available || variant.available
    group.quantity += variant.quantity
    if (!group.imageUrl && variant.imageUrl) group.imageUrl = variant.imageUrl
    groups.set(key, group)
  }
  return [...groups.values()]
}

/** Every variant of one product, from the variant-grain feed cube. */
export default function useProductVariantsQuery(itemGroupId: string) {
  const query = useMemo<Query>(() => {
    const filters: Filter[] = [
      {
        member: `${VARIANTS_CUBE}.item_group_id`,
        operator: "equals" as BinaryOperator,
        values: [itemGroupId],
      },
    ]
    return {
      dimensions: DIMENSIONS.map(d => `${VARIANTS_CUBE}.${d}`),
      filters,
      order: [[`${VARIANTS_CUBE}.id`, "asc"]],
      limit: MAX_VARIANTS,
    }
  }, [itemGroupId])

  const { resultSet, error } = useCubeQuery(query, { skip: !itemGroupId })

  // Latch the last resolved rows per product, like the drawer's row query:
  // useCubeQuery cancels its in-flight request whenever its effect re-runs
  // (StrictMode, the capabilities gate flipping), and a cancelled request
  // resolves `undefined` rather than rejecting. Reading resultSet directly
  // then shows "no variants" for a product that has plenty — before the
  // real response, or after it when the cancelled one lands late.
  const latch = useRef<{ key: string; rows: Record<string, unknown>[] | null }>(
    { key: itemGroupId, rows: null },
  )
  if (latch.current.key !== itemGroupId) {
    latch.current = { key: itemGroupId, rows: null }
  }
  if (resultSet) {
    latch.current.rows = resultSet.rawData() as Record<string, unknown>[]
  }
  const rows = latch.current.rows

  const variants = useMemo<TProductVariant[]>(() => {
    if (!rows) return []
    return rows
      .map(row => toVariant(row))
      .filter((v): v is TProductVariant => v !== null)
  }, [rows])

  const groups = useMemo(() => groupVariantsByColor(variants), [variants])

  // Loading until a result has resolved for THIS product (or it failed) —
  // not whatever the client's isLoading says between cancellations.
  return { variants, groups, isLoading: rows === null && !error, error }
}
